/**
 * Filtering for collections with more than 5000 products.
 *
 * Shopify does not populate `collection.filters` past 5000 products, and — verified
 * against the live storefront — it also ignores `filter.*` URL params on those
 * collections, both for normal page loads and for Section Rendering API requests.
 * So neither the facet list nor the filtered product set can come from Liquid.
 *
 * This module keeps the page visually identical to the normal Liquid path by:
 *
 *   1. reading facets from the Storefront API and injecting them into the SAME
 *      markup the section renders for a normal collection (mf-pill-bar flyouts +
 *      mobile accordions), so sections/collection-filter-bar.liquid's CSS and inline
 *      script apply unchanged;
 *   2. resolving filtered product *handles* + cursor pagination from the Storefront
 *      API, then fetching the real server-rendered `.mf-product-card` markup for
 *      those handles from sections/mf-card-batch.liquid via the Section Rendering
 *      API — so cards never drift from snippets/product-card.liquid.
 *
 * The server-rendered grid is left completely alone until the user actually touches
 * a filter or sort (or lands on a URL that already carries filter params). From that
 * point the module owns the grid and its pagination for the rest of the session.
 */
(function () {
  'use strict';

  var form = document.querySelector('[data-mf-api-mode]');
  if (!form) return;

  var grid = document.getElementById('CollectionProductGrid');
  if (!grid) return;

  // ── Config ────────────────────────────────────────────────────────────────────
  var cfg = {
    handle: form.dataset.collectionHandle,
    collectionUrl: form.dataset.collectionUrl || window.location.pathname,
    token: form.dataset.storefrontToken,
    apiVersion: form.dataset.apiVersion || '2024-04',
    sortBy: form.dataset.sortBy || '',
    totalCount: parseInt(form.dataset.productsCount, 10) || 0,
    resetLabel: form.dataset.resetLabel || 'Reset',
    clearAllLabel: form.dataset.clearAllLabel || 'Clear all',
    selectedLabel: form.dataset.selectedLabel || '__count__ selected',
    pageSize: parseInt(grid.dataset.pageSize, 10) || 24
  };
  var ENDPOINT = '/api/' + cfg.apiVersion + '/graphql.json';

  // `manual` and `most-relevant` have no distinct collection sort key —
  // COLLECTION_DEFAULT is the collection's own manual order, which is what both mean.
  var SORT_MAP = {
    'manual': { key: 'COLLECTION_DEFAULT', reverse: false },
    'most-relevant': { key: 'COLLECTION_DEFAULT', reverse: false },
    'best-selling': { key: 'BEST_SELLING', reverse: false },
    'title-ascending': { key: 'TITLE', reverse: false },
    'title-descending': { key: 'TITLE', reverse: true },
    'price-ascending': { key: 'PRICE', reverse: false },
    'price-descending': { key: 'PRICE', reverse: true },
    'created-ascending': { key: 'CREATED', reverse: false },
    'created-descending': { key: 'CREATED', reverse: true }
  };

  var QUERY = [
    'query($handle:String!,$first:Int!,$after:String,$filters:[ProductFilter!],$sortKey:ProductCollectionSortKeys,$reverse:Boolean){',
    '  collection(handle:$handle){',
    '    products(first:$first,after:$after,filters:$filters,sortKey:$sortKey,reverse:$reverse){',
    '      nodes{ handle }',
    '      pageInfo{ hasNextPage endCursor }',
    '      filters{ id label type values{ id label count input } }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  // ── State ─────────────────────────────────────────────────────────────────────
  var state = {
    active: false,        // module owns the grid + pagination
    skeleton: [],         // stable facet list from the unfiltered query
    inputMap: {},         // "name\nvalue" -> ProductFilter input object
    facets: [],           // skeleton + current counts, as rendered
    selected: {},         // name -> [value, …]
    sortBy: cfg.sortBy,
    cursor: null,
    hasNextPage: false,
    loading: false,
    requestId: 0,
    callouts: [],         // snapshot of the server-rendered editorial <li>s
    calloutsTaken: false
  };

  // ── DOM handles ───────────────────────────────────────────────────────────────
  // The server-rendered infinite-scroll link already carries the translated label;
  // reuse it for our cursor sentinel rather than hardcoding English.
  var loadMoreLabel = (function () {
    var a = document.querySelector('#AjaxinateInfiniteScrolling a');
    return (a && a.textContent.trim()) || 'Loading more';
  })();

  var dom = {
    pills: form.querySelector('.mf-pills'),
    mobileWrapper: form.querySelector('.facets__wrapper'),
    count: document.getElementById('collectionFilteredItemsCount'),
    countMobile: document.getElementById('collectionFilteredItemsCountMobile'),
    activeFacets: document.querySelector('.mf-active-facets'),
    container: document.getElementById('AjaxinateContainer'),
    list: null,
    pagination: null
  };
  dom.list = dom.container && dom.container.querySelector('ol');
  if (!dom.list) return;

  // ── Helpers ───────────────────────────────────────────────────────────────────
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function parseInput(input) {
    try { return typeof input === 'string' ? JSON.parse(input) : input; }
    catch (e) { return null; }
  }

  /**
   * Storefront filter values carry their selector as a JSON `input` blob. Shopify's
   * own URL params use the filter id as the key and the innermost scalar as the
   * value, e.g.
   *   {"productType":"Cushions"}                        -> filter.p.product_type=Cushions
   *   {"variantOption":{"name":"color","value":"Blue"}}  -> filter.v.option.color=Blue
   *   {"productMetafield":{…,"value":"Up To 50%"}}       -> filter.p.m.custom.sale=Up To 50%
   *   {"available":true}                                 -> filter.v.availability=1
   * Producing the same pairs keeps the URLs we push byte-identical to a normal
   * Shopify filtered URL.
   */
  function paramValueFromInput(input) {
    var obj = parseInput(input);
    if (obj == null) return null;
    var scalar = null;
    (function walk(node) {
      if (node == null || scalar !== null) return;
      if (typeof node === 'object') {
        if (Object.prototype.hasOwnProperty.call(node, 'value')) { walk(node.value); return; }
        for (var k in node) {
          if (Object.prototype.hasOwnProperty.call(node, k)) { walk(node[k]); return; }
        }
        return;
      }
      scalar = node;
    })(obj);
    if (scalar === null) return null;
    if (typeof scalar === 'boolean') return scalar ? '1' : '0';
    return String(scalar);
  }

  function selectedCount() {
    var n = 0;
    Object.keys(state.selected).forEach(function (k) { n += state.selected[k].length; });
    return n;
  }

  function activeFilterInputs() {
    var out = [];
    Object.keys(state.selected).forEach(function (name) {
      state.selected[name].forEach(function (value) {
        var input = state.inputMap[name + '\n' + value];
        if (input) out.push(input);
      });
    });
    return out;
  }

  function buildParams() {
    var params = new URLSearchParams();
    Object.keys(state.selected).sort().forEach(function (name) {
      state.selected[name].forEach(function (value) { params.append(name, value); });
    });
    if (state.sortBy) params.set('sort_by', state.sortBy);
    return params;
  }

  function urlFor(params) {
    var qs = params.toString();
    return cfg.collectionUrl + (qs ? '?' + qs : '');
  }

  /** URL with one value (or a whole group) removed — mirrors Liquid's url_to_remove. */
  function urlWithout(name, value) {
    var params = new URLSearchParams();
    Object.keys(state.selected).sort().forEach(function (n) {
      state.selected[n].forEach(function (v) {
        if (n === name && (value === null || v === value)) return;
        params.append(n, v);
      });
    });
    if (state.sortBy) params.set('sort_by', state.sortBy);
    return urlFor(params);
  }

  function clearAllHref() {
    var params = new URLSearchParams();
    if (state.sortBy) params.set('sort_by', state.sortBy);
    return urlFor(params);
  }

  function readSelectionFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var selected = {};
    params.forEach(function (value, name) {
      if (name.indexOf('filter.') !== 0) return;
      (selected[name] = selected[name] || []).push(value);
    });
    state.selected = selected;
    var sort = params.get('sort_by');
    if (sort) state.sortBy = sort;
    return selectedCount() > 0;
  }

  // ── Storefront API ────────────────────────────────────────────────────────────
  function graphql(variables) {
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token
      },
      body: JSON.stringify({ query: QUERY, variables: variables })
    }).then(function (r) {
      if (!r.ok) throw new Error('Storefront API ' + r.status);
      return r.json();
    }).then(function (body) {
      var products = body && body.data && body.data.collection && body.data.collection.products;
      if (!products) throw new Error('Storefront API returned no collection products');
      return products;
    });
  }

  function fetchPage(first, after, useFilters) {
    var sort = SORT_MAP[state.sortBy] || SORT_MAP.manual;
    var filters = useFilters ? activeFilterInputs() : [];
    return graphql({
      handle: cfg.handle,
      first: first,
      after: after || null,
      filters: filters.length ? filters : null,
      sortKey: sort.key,
      reverse: sort.reverse
    });
  }

  // ── Card HTML via the Section Rendering API ───────────────────────────────────
  function fetchCards(handles) {
    if (!handles.length) return Promise.resolve([]);
    var q = handles.map(function (h) { return 'handle:' + h; }).join(' OR ');
    var url = '/search?type=product&options%5Bunavailable_products%5D=last&q=' +
      encodeURIComponent(q) + '&section_id=mf-card-batch';
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('Card batch ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var byHandle = {};
        doc.querySelectorAll('#mf-card-batch > li[data-handle]').forEach(function (li) {
          byHandle[li.dataset.handle] = li;
        });
        // Search returns by relevance; re-emit in the order the Storefront API gave.
        // Handles the batch did not return (e.g. country-excluded, or not yet
        // indexed) are skipped rather than left as a hole in the grid.
        return handles.map(function (h) { return byHandle[h]; }).filter(Boolean);
      });
  }

  /**
   * mf-card-batch renders in search context, where `product.url | within: collection`
   * has no collection to scope to, so hrefs come back as /products/<handle>. Restore
   * the collection-scoped URL the Liquid grid produces.
   */
  function adoptCard(li) {
    li.querySelectorAll('a[href^="/products/"]').forEach(function (a) {
      a.setAttribute('href', cfg.collectionUrl + a.getAttribute('href'));
    });
    // The batch wrapper carries data-handle for ordering; the Liquid grid emits a
    // bare <li>, so strip it to keep the DOM identical.
    li.removeAttribute('data-handle');
    return li;
  }

  // ── Markup fragments copied from collection-filter-bar.liquid ─────────────────
  var CARET_SVG = '<svg aria-hidden="true" focusable="false" class="mf-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>';
  var ICON_CARET = '<svg aria-hidden="true" focusable="false" role="presentation" class="icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>';
  var CHECKBOX_SVGS =
    '<svg class="desktop-filter-checkbox" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"></rect></svg>' +
    '<svg class="desktop-filter-icon-checkmark icon-checkmark" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 9" fill="none" width="10px" height="10px"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.35.643a.5.5 0 01.006.707l-6.77 6.886a.5.5 0 01-.719-.006L.638 4.845a.5.5 0 11.724-.69l2.872 3.011 6.41-6.517a.5.5 0 01.707-.006h-.001z" fill="currentColor"/></svg>';
  var CLOSE_SVG = '<svg aria-hidden="true" focusable="false" width="10" height="10" viewBox="0 0 12 13" fill="none"><path d="M8.49 9.33L2.83 3.67" stroke="currentColor" stroke-linecap="round"/><path d="M2.89 9.39L8.43 3.62" stroke="currentColor" stroke-linecap="round"/></svg>';

  // ── Facets ────────────────────────────────────────────────────────────────────
  /**
   * Build the stable facet list from the UNFILTERED query. Later (filtered) responses
   * only supply counts: a filtered response can omit values entirely, and rebuilding
   * from it would make a selected option vanish from its flyout — leaving the user
   * unable to untick it. This mirrors how Liquid always renders the full
   * `filter.values` list and varies only the counts.
   */
  function buildSkeleton(apiFilters) {
    var out = [];
    (apiFilters || []).forEach(function (f) {
      // The Liquid `{% case filter.type %}` only handles boolean/list, so PRICE_RANGE
      // is skipped there too — skipping here keeps the two modes identical.
      if (f.type !== 'LIST') return;
      var values = [];
      (f.values || []).forEach(function (v) {
        var param = paramValueFromInput(v.input);
        if (param === null) return;
        state.inputMap[f.id + '\n' + param] = parseInput(v.input);
        values.push({ name: f.id, value: param, label: v.label, count: v.count });
      });
      if (values.length) out.push({ id: f.id, label: f.label, values: values });
    });
    state.skeleton = out;
  }

  /** Overlay the counts from a (possibly filtered) response onto the skeleton. */
  function applyCounts(apiFilters) {
    var counts = {};
    (apiFilters || []).forEach(function (f) {
      (f.values || []).forEach(function (v) {
        var param = paramValueFromInput(v.input);
        if (param !== null) counts[f.id + '\n' + param] = v.count;
      });
    });
    var hasCounts = Object.keys(counts).length > 0;

    state.facets = state.skeleton.map(function (f) {
      var picked = state.selected[f.id] || [];
      var values = f.values.map(function (v) {
        var key = v.name + '\n' + v.value;
        return {
          name: v.name,
          value: v.value,
          label: v.label,
          count: hasCounts ? (counts[key] || 0) : v.count,
          active: picked.indexOf(v.value) !== -1
        };
      });
      return {
        id: f.id,
        label: f.label,
        values: values,
        activeCount: values.filter(function (v) { return v.active; }).length
      };
    });
  }

  /** Desktop pill flyouts — mirrors collection-filter-bar.liquid lines 233-259. */
  function renderPills() {
    if (!dom.pills) return;
    var html = '';
    state.facets.forEach(function (f) {
      html += '<details class="mf-pill-dd" data-mf-flyout>' +
        '<summary class="mf-pill mf-pill--dd">' +
        '<span>' + esc(f.label) + '</span>' +
        (f.activeCount > 0 ? '<span class="mf-pill__count">' + f.activeCount + '</span>' : '') +
        CARET_SVG +
        '</summary>' +
        '<div class="mf-flyout"><ul class="mf-flyout__list" role="list">';
      f.values.forEach(function (v) {
        var disabled = v.count === 0 && !v.active;
        html += '<li><label class="mf-flyout__opt' + (disabled ? ' mf-flyout__opt--disabled' : '') + '">' +
          '<input type="checkbox" name="' + esc(v.name) + '" value="' + esc(v.value) + '"' +
          (v.active ? ' checked' : '') + (disabled ? ' disabled' : '') + '>' +
          '<span>' + esc(v.label) + ' (' + v.count + ')</span>' +
          '</label></li>';
      });
      html += '</ul><div class="mf-flyout__foot">' +
        '<a href="' + esc(urlWithout(f.id, null)) + '" class="mf-flyout__reset js-facet-remove">' +
        esc(cfg.resetLabel) + '</a>' +
        '</div></div></details>';
    });

    // Preserve the open flyout so the panel does not close under the user when they
    // tick a box, and keep the server-rendered "All" pill in place.
    var openId = null;
    var openEl = dom.pills.querySelector('details.mf-pill-dd[open]');
    if (openEl) {
      var s = openEl.querySelector('summary > span');
      openId = s && s.textContent;
    }
    dom.pills.querySelectorAll('.mf-pill-dd').forEach(function (el) { el.remove(); });
    dom.pills.insertAdjacentHTML('beforeend', html);
    if (openId) {
      dom.pills.querySelectorAll('details.mf-pill-dd').forEach(function (d) {
        var s = d.querySelector('summary > span');
        if (s && s.textContent === openId) d.setAttribute('open', '');
      });
    }
  }

  /** Mobile accordions — mirrors collection-filter-bar.liquid lines 303-335. */
  function renderMobileFacets() {
    if (!dom.mobileWrapper) return;
    var html = '';
    state.facets.forEach(function (f, i) {
      var selectedText = String(cfg.selectedLabel).replace('__count__', f.activeCount);
      html += '<details class="desktop-filters-view js-filter" data-index="' + (i + 1) + '">' +
        '<summary class="facets__summary desktop-filters-view-fassets-summary">' +
        '<span>' + esc(f.label) + '</span>' +
        '<span class="count-bubble">' + (f.activeCount > 0 ? f.activeCount : '') + '</span>' +
        ICON_CARET +
        '</summary>' +
        '<ul class="facets__list list-unstyled desktop-filters-ul-list" role="list">';
      f.values.forEach(function (v, j) {
        var disabled = v.count === 0 && !v.active;
        var id = 'Filter-' + f.label + '-' + (j + 1);
        html += '<li class="list-menu__item facets__item">' +
          '<label for="' + esc(id) + '" class="desktop-facet-checkbox facet-checkbox' +
          (disabled ? ' facet-checkbox--disabled' : '') + '">' +
          '<input class="desktop-filter-input" type="checkbox" name="' + esc(v.name) +
          '" value="' + esc(v.value) + '" id="' + esc(id) + '"' +
          (v.active ? ' checked' : '') + (disabled ? ' disabled' : '') + '>' +
          '<span class="facet-checkbox-visual">' + CHECKBOX_SVGS + '</span>' +
          '<span class="facet-label-text">' + esc(v.label) + ' (' + v.count + ')</span>' +
          '</label></li>';
      });
      html += '</ul><div class="facets__display"><div class="facets__header">' +
        '<span class="facets__selected no-js-hidden">' + esc(selectedText) + '</span>' +
        '<a href="' + esc(urlWithout(f.id, null)) + '" class="facets__reset link underlined-link js-facet-remove">' +
        esc(cfg.resetLabel) + '</a>' +
        '</div></div></details>';
    });

    var open = {};
    dom.mobileWrapper.querySelectorAll('details.js-filter[open]').forEach(function (d) {
      open[d.dataset.index] = true;
    });
    dom.mobileWrapper.querySelectorAll('details.js-filter').forEach(function (el) { el.remove(); });
    var noscript = dom.mobileWrapper.querySelector('noscript');
    if (noscript) noscript.insertAdjacentHTML('beforebegin', html);
    else dom.mobileWrapper.insertAdjacentHTML('beforeend', html);
    dom.mobileWrapper.querySelectorAll('details.js-filter').forEach(function (d) {
      if (open[d.dataset.index]) d.setAttribute('open', '');
    });
  }

  /** Active chips — mirrors collection-filter-bar.liquid lines 369-384. */
  function renderActiveFacets() {
    if (!dom.activeFacets) return;
    var html = '';
    state.facets.forEach(function (f) {
      f.values.forEach(function (v) {
        if (!v.active) return;
        html += '<a class="mf-active-tag active-facets__button js-facet-remove button-active-tag" href="' +
          esc(urlWithout(v.name, v.value)) + '">' + esc(v.label) + ' ' + CLOSE_SVG + '</a>';
      });
    });
    if (selectedCount() > 1) {
      html += '<a href="' + esc(clearAllHref()) + '" class="mf-active-clear">' + esc(cfg.clearAllLabel) + '</a>';
    }
    dom.activeFacets.innerHTML = html;
  }

  function renderCount(total) {
    var text = String(total);
    if (dom.count) dom.count.textContent = text;
    if (dom.countMobile) dom.countMobile.textContent = text;
  }

  function paintFacets() {
    renderPills();
    renderMobileFacets();
    renderActiveFacets();
    if (window.MFFilterBar) {
      window.MFFilterBar.refreshBadges();
      window.MFFilterBar.syncDesktopFlyouts();
    }
  }

  /**
   * ProductConnection exposes no totalCount, so derive it from the facets returned
   * alongside the filtered page. Shopify computes an *active* group's own counts with
   * every OTHER active filter applied, so summing the selected values of one active
   * group gives the exact result total. (Summing a non-active group does not work:
   * vendor truncates at 100 values and multi-valued metafields double-count.)
   */
  function deriveTotal(apiFilters, loaded, hasNext) {
    if (selectedCount() === 0) return cfg.totalCount;
    for (var i = 0; i < (apiFilters || []).length; i++) {
      var f = apiFilters[i];
      var picked = state.selected[f.id];
      if (!picked || !picked.length) continue;
      var sum = 0;
      var matched = 0;
      (f.values || []).forEach(function (v) {
        var param = paramValueFromInput(v.input);
        if (param !== null && picked.indexOf(param) !== -1) { sum += v.count; matched++; }
      });
      if (matched === picked.length) return sum;
    }
    return hasNext ? loaded + '+' : loaded;
  }

  // ── Pagination ────────────────────────────────────────────────────────────────
  var observer = null;

  function paginationEl() {
    if (dom.pagination && document.body.contains(dom.pagination)) return dom.pagination;
    var wrap = grid.querySelector('.collection-pagination');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'collection-pagination justify-content-center';
      (grid.querySelector('.page-width') || grid).appendChild(wrap);
    }
    var inner = wrap.querySelector('#AjaxinateInfiniteScrolling');
    if (!inner) {
      inner = document.createElement('div');
      inner.id = 'AjaxinateInfiniteScrolling';
      wrap.appendChild(inner);
    }
    dom.pagination = inner;
    return inner;
  }

  function updatePagination() {
    var el = paginationEl();
    if (observer) observer.disconnect();
    if (!state.hasNextPage) {
      el.innerHTML = '';
      return;
    }
    el.innerHTML = '<a href="#" data-mf-load-more>' + esc(loadMoreLabel) + '</a>';
    if (!observer) {
      // 500px lead-in matches the native scroller in collection-grid-redesign.liquid.
      observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) loadMore();
      }, { rootMargin: '500px 0px' });
    }
    observer.observe(el);
  }

  function loadMore() {
    if (state.loading || !state.hasNextPage) return;
    render(true);
  }

  // ── Editorial callouts ────────────────────────────────────────────────────────
  // The Liquid path re-renders the callout/banner <li>s on the first page of every
  // filtered result set. Snapshot them once, then re-insert at the same positions.
  function snapshotCallouts() {
    if (state.calloutsTaken) return;
    state.calloutsTaken = true;
    Array.prototype.slice.call(dom.list.children).forEach(function (li, index) {
      if (li.classList.contains('mf-callout') || li.classList.contains('mf-banner')) {
        state.callouts.push({ index: index, node: li.cloneNode(true) });
      }
    });
  }

  function reinsertCallouts() {
    state.callouts.forEach(function (c) {
      dom.list.insertBefore(c.node.cloneNode(true), dom.list.children[c.index] || null);
    });
  }

  // ── Main render ───────────────────────────────────────────────────────────────
  function render(append) {
    if (state.loading) return Promise.resolve();
    state.loading = true;
    var id = ++state.requestId;
    var pageWidth = grid.querySelector('.page-width');
    if (pageWidth) pageWidth.classList.add('loading');

    return fetchPage(cfg.pageSize, append ? state.cursor : null, true)
      .then(function (products) {
        if (id !== state.requestId) return null;
        var handles = (products.nodes || []).map(function (n) { return n.handle; });
        state.cursor = products.pageInfo.endCursor;
        state.hasNextPage = products.pageInfo.hasNextPage;
        return fetchCards(handles).then(function (items) {
          return { products: products, items: items };
        });
      })
      .then(function (result) {
        if (!result || id !== state.requestId) return;

        if (!append) dom.list.innerHTML = '';
        var frag = document.createDocumentFragment();
        result.items.forEach(function (li) { frag.appendChild(adoptCard(li)); });
        dom.list.appendChild(frag);

        if (!append) {
          reinsertCallouts();
          applyCounts(result.products.filters);
          paintFacets();
          renderCount(deriveTotal(result.products.filters, result.items.length, state.hasNextPage));
        }

        updatePagination();
        // product-card.js rehydrates the cards through its own MutationObserver;
        // Swym needs this explicit nudge (see snippets/swym-custom.liquid).
        document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
      })
      .catch(function (err) {
        if (window.console) console.error('[mf-large-collection-filtering]', err);
      })
      .then(function () {
        if (id === state.requestId) {
          state.loading = false;
          if (pageWidth) pageWidth.classList.remove('loading');
        }
      });
  }

  /**
   * Take the grid over. Once this happens the module owns pagination for the rest of
   * the session — including after the user clears every filter, because our pagination
   * element holds a cursor sentinel rather than a Liquid `paginate.next.url`.
   */
  function takeOver() {
    if (state.active) return;
    state.active = true;
    snapshotCallouts();
    document.documentElement.setAttribute('data-mf-api-paging', '1');
  }

  function apply(pushUrl) {
    takeOver();
    state.cursor = null;
    state.hasNextPage = false;
    if (pushUrl !== false) history.pushState({ mfApi: true }, '', urlFor(buildParams()));
    return render(false);
  }

  // ── Mobile drawer ─────────────────────────────────────────────────────────────
  // In Liquid mode this is wired by filtering.aio.min.js (the CollectionFiltersForm
  // custom element), which API mode deliberately does not load. Same class contract:
  // `collection-filtering-form.is-open` is what theme.min.css keys the slide-in off
  // (`collection-filtering-form.is-open .facets__form`).
  (function drawer() {
    var openBtn = document.querySelector('[data-drawer-open-btn]');
    var panel = form.querySelector('#CollectionFiltersForm');
    if (!openBtn || !panel) return;

    function offClick(e) {
      if (!panel.contains(e.target)) close();
    }

    function close() {
      document.body.classList.remove('js-drawer-open-left', 'js-drawer-open');
      form.classList.remove('is-open');
      openBtn.setAttribute('tabIndex', '0');
      document.removeEventListener('click', offClick, true);
    }

    function open(e) {
      e.preventDefault();
      e.currentTarget.setAttribute('tabIndex', '-1');
      form.classList.add('is-open');
      document.body.classList.add('js-drawer-open-left', 'js-drawer-open');
      // Registered during this click's target phase, so it is not invoked for the
      // click that opened the drawer.
      document.addEventListener('click', offClick, true);
    }

    openBtn.addEventListener('click', open);
    form.querySelectorAll('[data-drawer-close-btn]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    // Mirrors addDrawerListeners(): the panel is only a modal at drawer widths.
    function syncModalRole() {
      if (window.innerWidth < 991) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
        close();
      }
    }
    window.addEventListener('resize', syncModalRole);
    syncModalRole();
  })();

  // ── Events ────────────────────────────────────────────────────────────────────
  function setSelection(name, value, on) {
    var list = state.selected[name] || [];
    var i = list.indexOf(value);
    if (on && i === -1) list.push(value);
    if (!on && i !== -1) list.splice(i, 1);
    if (list.length) state.selected[name] = list;
    else delete state.selected[name];
  }

  form.addEventListener('change', function (e) {
    var el = e.target;
    if (el.matches && el.matches('input[type="checkbox"][name^="filter."]')) {
      setSelection(el.name, el.value, el.checked);
      apply();
    } else if (el.matches && el.matches('select[name="sort_by"]')) {
      state.sortBy = el.value;
      apply();
    }
  });

  // Facet resets, active chips and the "All" pill are plain <a href> in both modes.
  // Intercept them so they filter in place instead of navigating to a URL the server
  // would render unfiltered. Capture phase so navigation is cancelled before the
  // section's own bubble-phase checkbox-sync handler runs.
  document.addEventListener('click', function (e) {
    var link = e.target.closest &&
      e.target.closest('.js-facet-remove, #ClearAllFilters, .mf-active-clear, [data-mf-load-more]');
    if (!link) return;
    e.preventDefault();

    if (link.hasAttribute('data-mf-load-more')) { loadMore(); return; }

    var params;
    try { params = new URL(link.getAttribute('href'), window.location.origin).searchParams; }
    catch (err) { return; }
    var selected = {};
    params.forEach(function (value, name) {
      if (name.indexOf('filter.') !== 0) return;
      (selected[name] = selected[name] || []).push(value);
    });
    state.selected = selected;
    apply();
  }, true);

  window.addEventListener('popstate', function () {
    readSelectionFromUrl();
    if (!state.active && selectedCount() === 0) return;
    apply(false);
  });

  // ── Boot ──────────────────────────────────────────────────────────────────────
  // Always load the unfiltered facet list first: it is the stable skeleton, and it is
  // what maps URL params back to ProductFilter inputs. Only then, if the URL already
  // carries filters, take the grid over.
  var hasUrlFilters = readSelectionFromUrl();

  fetchPage(1, null, false)
    .then(function (products) {
      buildSkeleton(products.filters);
      applyCounts(hasUrlFilters ? null : products.filters);
      paintFacets();
      if (hasUrlFilters) {
        takeOver();
        return render(false);
      }
    })
    .catch(function (err) {
      if (window.console) console.error('[mf-large-collection-filtering]', err);
    });
})();
